<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import MainMenu from '@/components/MainMenu.vue'
import InstitutionSelector from '@/components/InstitutionSelector.vue'
import { RootState } from '@/store/types'
import { Assets } from '@/types/assets'
import VentanaMantenimiento from '@/components/mantenimiento/VentanaMantenimiento.vue'
import AlertaExpiracionFirmantes from '@/components/mantenimiento/AlertaExpiracionFirmantes.vue'
import { convertFirestoreTimestamp, obtenerBitacora, listenBitacoraContractDate } from '@/components/utils/functions'
import { formatDate } from '@/utils/dateFormatter'

const store = useStore<RootState>()
const router = useRouter()
const isMenuOpen = ref(false)
const windowWidth = ref(window.innerWidth)
const assets = Assets.getInstance()
const selectedInstitutionResponsables = computed(() => store.state.global.selectedInstitution.Responsable)

// Update window width on resize
const handleResize = () => {
	windowWidth.value = window.innerWidth
}
let stopContractDateListener: null | (() => void) = null

const startContractDateListener = () => {
	stopContractDateListener?.()
	const idInstitution = store.getters['global/currentInstitution']
	if (!idInstitution) {
		store.commit('SET_CONTRACT_DATE', { fechaInicioContrato: null, fechaFinContrato: null })
		return
	}
	stopContractDateListener = listenBitacoraContractDate(idInstitution, contractDate => {
		store.commit('SET_CONTRACT_DATE', contractDate ?? { fechaInicioContrato: null, fechaFinContrato: null })
	})
}

onMounted(() => {
	window.addEventListener('resize', handleResize)
	obtenerBitacora(store.getters['global/currentInstitution']).then(bitacora => {
		store.commit('SET_CONTRACT_DATE', {
			fechaInicioContrato: bitacora?.fechaInicioContrato ?? null,
			fechaFinContrato: bitacora?.fechaFinContrato ?? null
		})
	})
	startContractDateListener()
})

watch(
	() => store.getters['global/currentInstitution'],
	() => {
		startContractDateListener()
	}
)

onUnmounted(() => {
	window.removeEventListener('resize', handleResize)
	stopContractDateListener?.()
	stopContractDateListener = null
})

// Test mode from store
const isTestMode = computed({
	get: () => store.getters.isTestMode,
	set: () => store.dispatch('toggleTestMode')
})

// Toggle menu
const toggleMenu = () => {
	isMenuOpen.value = !isMenuOpen.value
}

// Handle logout
const handleLogout = async () => {
	try {
		await store.dispatch('logout')
		await router.push('/login')
	} catch (error) {
		console.error('Error durante el cierre de sesión:', error)
	}
}

// Handle mode change
const handleModeChange = () => {
	store.dispatch('toggleTestMode')
}

const isNearExpiry = computed(() => {
	if (!store.getters.getContractDate.fechaFinContrato) return false
	const expiryDate = new Date(convertFirestoreTimestamp(store.getters.getContractDate.fechaFinContrato))
	const today = new Date()
	const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
	return daysUntilExpiry <= 30 && daysUntilExpiry > 0
})

const daysUntilExpiry = computed(() => {
	if (!store.getters.getContractDate.fechaFinContrato) return 0
	const expiryDate = new Date(convertFirestoreTimestamp(store.getters.getContractDate.fechaFinContrato))
	const today = new Date()
	return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
})

const isExpired = computed(() => {
	if (!store.getters.getContractDate.fechaFinContrato) return false
	const expiryDate = new Date(convertFirestoreTimestamp(store.getters.getContractDate.fechaFinContrato))
	const today = new Date()
	return expiryDate < today
})
</script>

<template>
	<div class="main-layout">
		<!-- Navbar -->
		<VaNavbar color="primary" :class="{ 'test-mode': isTestMode }">
			<template #left>
				<button class="menu-toggle" @click="toggleMenu" style="margin-right: 10px">
					<i :class="['fa', isMenuOpen ? 'fa-times' : 'fa-bars']" style="color: #ffffff"></i>
				</button>
				<VaNavbarItem class="logo-container" style="font-size: 1.2rem; font-weight: bold">
					<img v-if="assets.logoMenu" :src="assets.logoMenu" alt="Logo" class="logo" />
					<span v-else>IPES Alumni</span>
				</VaNavbarItem>
			</template>
			<template #center>
				<VaNavbarItem> <InstitutionSelector /> </VaNavbarItem>
			</template>
			<template #right>
				<VaNavbarItem class="hidden sm:block">
					<VaBadge :text="isTestMode ? 'Modo Prueba' : 'Modo Producción'" :color="!isTestMode ? 'primary' : '#dc3545'" class="ml-2 va-badge__text" />
				</VaNavbarItem>
			</template>
		</VaNavbar>
		<div class="contract-alerts">
			<VaAlert v-if="isNearExpiry" color="warning" class="contract-alert">
				<template #icon>
					<VaIcon name="calendar_month" />
				</template>
				<div class="contract-alert__content">
					<strong class="contract-alert__title">Contrato próximo a vencer</strong>
					<p class="contract-alert__date">Fecha de vencimiento: {{ formatDate(convertFirestoreTimestamp(store.getters.getContractDate.fechaFinContrato)) }}</p>
					<p class="contract-alert__days">
						Vence en <span>{{ daysUntilExpiry }}</span> días
					</p>
				</div>
			</VaAlert>
			<VaAlert v-if="isExpired" color="danger" class="contract-alert">
				<template #icon>
					<VaIcon name="calendar_month" />
				</template>
				<div class="contract-alert__content">
					<strong class="contract-alert__title">Contrato vencido</strong>
					<p class="contract-alert__date">Venció el {{ formatDate(convertFirestoreTimestamp(store.getters.getContractDate.fechaFinContrato)) }}</p>
				</div>
			</VaAlert>
		</div>
		<div class="main-layout__body">
			<VentanaMantenimiento />
			<AlertaExpiracionFirmantes :selectedInstitutionResponsables="selectedInstitutionResponsables" />
			<MainMenu v-model:isMenuOpen="isMenuOpen" v-model:isTestMode="isTestMode" @logout="handleLogout" @mode-change="handleModeChange"> </MainMenu>
		</div>
	</div>
</template>

<style scoped>
.contract-alerts {
	padding: 0.75rem 1rem;
	max-width: 100%;
	background-color: #ffffff;
}

.contract-alert {
	border-radius: 8px;
}

.contract-alert__content {
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
}

.contract-alert__title {
	display: block;
	font-size: 1rem;
	margin: 0;
}

.contract-alert__date {
	margin: 0;
	font-size: 0.9rem;
	opacity: 0.95;
}

.contract-alert__days {
	margin: 0;
	font-size: 0.9rem;
}

.logo-container {
	background-color: #ffffff55;
	border-radius: 5px;
}
.logo {
	width: 100px;
	padding: 5px;
	border-radius: 5px;
}
.main-layout {
	height: 100vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.main-layout__body {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

.menu-toggle {
	background: none;
	border: none;
	font-size: 1.2rem;
	color: #333;
	cursor: pointer;
	padding: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: color 0.3s ease;
}

.menu-toggle:hover {
	color: #062342;
}
.va-badge__text {
	text-transform: lowercase !important;
}

.navbar.test-mode .menu-toggle:hover {
	color: #ffcdd2;
}
</style>
